<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Produits ({{ totalRows }})</h1>
        <div class="d-flex gap-2">
          <ModuleActivityButton :module="['products', 'Product', 'product_photos']" label="Produits" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button v-if="canImportProducts" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="openImportDialog">
            <i class="fa fa-file-excel"></i>
            <span class="btn-label ms-1">Importer</span>
          </button>

          <button v-if="canMergeProducts" class="btn btn-outline-primary btn-sm btn-icon-label" @click="openMergeDialog">
            <i class="fa fa-code-merge"></i>
            <span class="btn-label ms-1">Fusionner</span>
          </button>

          <button v-if="canCreateProduct" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouveau produit</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <!-- ✅ FILTRES -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <!-- catégorie -->
            <div class="col-md-3">
              <label class="form-label small mb-1">Catégorie</label>
              <PrimevueDropdown
                v-model="filters.category_id"
                :options="categoryFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                filter
              >
                <template #value>
                  <span class="rayon-option">
                    <span
                      v-if="selectedRayonFilterOption.value"
                      class="rayon-color-dot"
                      :style="{ backgroundColor: rayonColor(selectedRayonFilterOption.color) }"
                    ></span>
                    <span>{{ selectedRayonFilterOption.label }}</span>
                  </span>
                </template>
                <template #option="{ option }">
                  <span class="rayon-option">
                    <span
                      v-if="option.value"
                      class="rayon-color-dot"
                      :style="{ backgroundColor: rayonColor(option.color) }"
                    ></span>
                    <span>{{ option.label }}</span>
                  </span>
                </template>
              </PrimevueDropdown>
              <small v-if="isLoadingFilters" class="text-muted small">
                Chargement...
              </small>
            </div>

            <!-- rayon -->
            <div class="col-md-3">
              <label class="form-label small mb-1">Rayon</label>
              <PrimevueDropdown
                v-model="filters.rayon_id"
                :options="rayonFilterOptions"
                optionLabel="label"
                optionValue="value"
                class="w-100"
                filter
              />
              <small v-if="isLoadingFilters" class="text-muted small">
                Chargement...
              </small>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchProducts">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="productsResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canDeleteProducts"
        v-model:selected-row-ids="bulkSelectedIds"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="products.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom, code ou code-barres..."
        detail-route-name="ProductShow"
        empty-text="Aucun produit trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchProducts"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            :disabled="bulkActionPending || isOverLimit"
            @click="bulkDeactivateProducts"
          >
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-photo="{ row }">
          <div class="text-center">
            <img
              :src="productImage(row)"
              alt="photo produit"
              style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px;"
            />
          </div>
        </template>

        <template #cell-rayon="{ row }">
          <span class="rayon-badge" :style="{ borderColor: rayonColor(row.rayon_color) }">
            <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(row.rayon_color) }"></span>
            <span>{{ row.rayon || '-' }}</span>
          </span>
        </template>

        <template #cell-categorie="{ row }">
          {{ row.categorie || '-' }}
        </template>

        <template #cell-sale_price="{ row }">
          <div class="text-end">
            <div v-if="row.has_active_promotion" class="fw-semibold text-success">
              {{ $format.currency(row.effective_sale_price || 0) }}
            </div>
            <div :class="row.has_active_promotion ? 'text-muted text-decoration-line-through small' : 'fw-semibold'">
              {{ $format.currency(row.sale_price || 0) }}
            </div>
            <span v-if="row.has_active_promotion" class="badge bg-success-subtle text-success border border-success-subtle">
              Promo
            </span>
          </div>
        </template>

        <template #cell-sale_price_coefficient="{ row }">
          <div class="text-end">
            <span
              v-if="row.sale_price_coefficient !== null && row.sale_price_coefficient !== undefined"
              class="badge"
              :class="Number(row.sale_price_coefficient) <= 1 ? 'bg-danger' : 'bg-success'"
            >
              x{{ Number(row.sale_price_coefficient).toFixed(2) }}
            </span>
            <span v-else class="text-muted">—</span>
          </div>
        </template>

        <template #cell-stock_quantity="{ row }">
          <span
            :class="[
              'badge',
              row.stock_quantity <= row.min_stock_quantity
                ? 'bg-danger'
                : 'bg-success'
            ]"
          >
            {{ row.stock_quantity }}
          </span>
        </template>

        <template #cell-allow_negative_stock_sales="{ row }">
          <span
            class="badge"
            :class="row.allow_negative_stock_sales ? 'bg-warning text-dark' : 'bg-secondary'"
          >
            {{ row.allow_negative_stock_sales ? 'Autorisé' : 'Bloqué' }}
          </span>
        </template>

      </BootstrapDataTable>

      <!-- Dialog création / modification -->
      <PrimevueDialog v-model:visible="dialogVisible" :modal="true" :closable="false" :style="{ width: '900px', maxWidth: '95vw' }">
        <template #header>
          <h5 class="modal-title mb-0">
            {{ dialogMode === 'create' ? 'Nouveau produit' : 'Modifier le produit' }}
          </h5>
        </template>

        <ProductForm :mode="dialogMode" :product="selectedProduct" @saved="handleSaved" @cancel="closeDialog" />
      </PrimevueDialog>

      <ProductMergeDialog
        v-model:visible="mergeDialogVisible"
        :categories="categories"
        :rayons="rayons"
        @merged="handleMerged"
      />

      <!-- MODAL IMPORT EXCEL -->
      <PrimevueDialog
        v-model:visible="importDialogVisible"
        :modal="true"
        :style="{ width: '920px', maxWidth: '96vw' }"
        :closable="false"
      >
        <template #header>
          <h5 class="mb-0">
            <i class="fa fa-file-excel me-2 text-success"></i>
            Importer des produits depuis Excel
          </h5>
        </template>

        <div class="import-steps mb-3">
          <span class="step-pill" :class="{ active: importStep === 'guide' }">
            1. Préparer le fichier
          </span>
          <span class="step-pill" :class="{ active: importStep === 'results' }">
            2. Résultats
          </span>
        </div>

        <div v-if="importStep === 'guide'">
          <div class="row g-3">
            <div class="col-lg-5">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body">
                  <h6 class="mb-2">Colonnes acceptées</h6>
                  <div class="small text-muted mb-2">
                    Les en-têtes peuvent être en français ou en anglais.
                  </div>

                  <ul class="small mb-3">
                    <li><strong>Obligatoires :</strong> nom, sku/code, prix_vente ou coefficient, stock</li>
                    <li><strong>Optionnelles :</strong> code_barre, prix_achat, tva, css, stock_minimum, rayon, categorie, description, photo_url/photo, reference_fournisseur, delai_livraison, quantite_minimale, actif</li>
                  </ul>

                  <div class="mb-3">
                    <label class="form-label small mb-1">Fournisseur à associer</label>
                    <PrimevueDropdown
                      v-model="selectedImportSupplierId"
                      :options="supplierImportOptions"
                      optionLabel="label"
                      optionValue="value"
                      class="w-100"
                      filter
                      placeholder="Sélectionner un fournisseur"
                    />
                    <small class="text-muted">
                      Le fournisseur système est sélectionné par défaut pour rattacher les produits importés avec leur prix d’achat.
                    </small>
                  </div>

                  <div class="small text-muted mb-3">
                    La colonne <strong>photo_url</strong> ou <strong>photo</strong> doit contenir une URL publique de l’image du produit.
                  </div>

                  <div class="d-flex flex-wrap gap-2">
                    <button type="button" class="btn btn-outline-success btn-sm btn-icon-label" @click="downloadTemplate">
                      <i class="fa fa-download"></i>
                      <span class="btn-label ms-1">Modèle Excel</span>
                    </button>
                    <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="triggerFileSelect">
                      <i class="fa fa-folder-open"></i>
                      <span class="btn-label ms-1">Choisir un fichier</span>
                    </button>
                  </div>

                  <input
                    ref="fileInput"
                    type="file"
                    class="d-none"
                    accept=".xlsx,.xls,.csv"
                    @change="onFileChange"
                  />

                  <div v-if="selectedFileName" class="mt-3 small">
                    <span class="text-muted">Fichier :</span>
                    <strong>{{ selectedFileName }}</strong>
                  </div>

                  <div v-if="isParsing" class="mt-3 small text-muted">
                    <i class="fa fa-spinner fa-spin me-1"></i>
                    Lecture du fichier...
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-7">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">Aperçu des lignes</h6>
                    <span class="badge bg-light text-dark">{{ parsedRows.length }} ligne(s)</span>
                  </div>

                  <div v-if="parseErrorMessage" class="alert alert-danger py-2">
                    {{ parseErrorMessage }}
                  </div>

                  <div v-if="parsedRows.length" class="table-responsive">
                    <table class="table table-sm table-striped table-bordered align-middle mb-0">
                      <thead class="table-light">
                        <tr>
                          <th>Ligne</th>
                          <th>Nom</th>
                          <th>SKU</th>
                          <th>Code-barres</th>
                          <th class="text-end">Prix vente</th>
                          <th class="text-end">Coef.</th>
                          <th>TVA</th>
                          <th>CSS</th>
                          <th class="text-center">Stock</th>
                          <th>Rayon</th>
                          <th>Catégorie</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="row in previewRows" :key="row.__rowNum__">
                          <td>{{ row.__rowNum__ }}</td>
                          <td>{{ row.name || '-' }}</td>
                          <td>{{ row.sku || '-' }}</td>
                          <td>{{ row.barcode || '-' }}</td>
                          <td class="text-end">{{ row.sale_price || '-' }}</td>
                          <td class="text-end">{{ row.sale_price_coefficient || '-' }}</td>
                          <td>{{ row.is_tva_applicable || '-' }}</td>
                          <td>{{ row.is_css_applicable || '-' }}</td>
                          <td class="text-center">{{ row.stock_quantity || '-' }}</td>
                          <td>{{ row.rayon || '-' }}</td>
                          <td>{{ row.categorie || '-' }}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="parsedRows.length > previewRows.length" class="small text-muted mt-2">
                      Affichage des {{ previewRows.length }} premières lignes seulement.
                    </div>
                  </div>

                  <div v-else class="text-center text-muted py-4">
                    Sélectionne un fichier Excel pour afficher un aperçu.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body">
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div>
                  <h6 class="mb-1">Traitement de l’import</h6>
                  <small class="text-muted">
                    Import #{{ importResult?.id || '-' }}
                  </small>
                </div>
                <span class="badge" :class="importStatusClass(importResult?.status)">
                  {{ importStatusLabel(importResult?.status) }}
                </span>
              </div>

              <div class="progress mb-2" style="height: 8px;">
                <div
                  class="progress-bar"
                  role="progressbar"
                  :style="{ width: `${importProgressPercent}%` }"
                  :aria-valuenow="importProgressPercent"
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>

              <div class="small text-muted">
                {{ importResult?.processed_rows || 0 }} / {{ importResult?.total_rows || parsedRows.length }} ligne(s) traitée(s)
              </div>

              <div v-if="importResult?.error_message" class="alert alert-danger py-2 mt-3 mb-0">
                {{ importResult.error_message }}
              </div>
            </div>
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">Importés</div>
                  <div class="h4 mb-0 text-success">{{ importResult?.imported_count || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">Ignorés</div>
                  <div class="h4 mb-0 text-warning">{{ importResult?.skipped_count || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-0 shadow-sm">
                <div class="card-body py-3">
                  <div class="small text-muted">En erreur</div>
                  <div class="h4 mb-0 text-danger">{{ importResult?.failed_count || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="table-responsive import-results-table">
            <table class="table table-sm table-striped table-hover table-bordered align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Ligne</th>
                  <th>Produit</th>
                  <th>SKU</th>
                  <th>Statut</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in importResultRows" :key="`${row.row_number}-${row.sku}`">
                  <td>{{ row.row_number }}</td>
                  <td>{{ row.name || '-' }}</td>
                  <td>{{ row.sku || '-' }}</td>
                  <td>
                    <span class="badge" :class="importRowStatusClass(row.status)">
                      {{ importRowStatusLabel(row.status) }}
                    </span>
                  </td>
                  <td>{{ row.message || '-' }}</td>
                </tr>
                <tr v-if="!importResultRows.length">
                  <td colspan="5" class="text-center text-muted py-4">
                    Les résultats détaillés apparaîtront pendant le traitement.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <template #footer>
          <div class="d-flex justify-content-between align-items-center gap-2 w-100">
            <button
              v-if="importStep === 'results'"
              class="btn btn-outline-secondary btn-sm btn-icon-label"
              type="button"
              :disabled="isImporting"
              @click="importStep = 'guide'"
            >
              <i class="fa fa-arrow-left"></i>
              <span class="btn-label ms-1">Préparation</span>
            </button>
            <span v-else></span>

            <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary btn-sm" type="button" @click="closeImportDialog">
          <i class="fa fa-xmark me-1" aria-hidden="true"></i>
          Fermer
            </button>
            <button
              v-if="importStep === 'guide'"
              class="btn btn-primary btn-sm"
              type="button"
              @click="submitImport"
              :disabled="!parsedRows.length || isImporting"
            >
              <span v-if="isImporting">
                <i class="fa fa-spinner fa-spin me-1"></i>
                Import en cours...
              </span>
              <span v-else>
                <i class="fa fa-upload me-1"></i>
                Lancer l’import
              </span>
            </button>
            <button
              v-else
              class="btn btn-outline-primary btn-sm btn-icon-label"
              type="button"
              :disabled="!importResult?.id || isImporting"
              @click="fetchImportStatus"
            >
              <i class="fa fa-rotate-right"></i>
              <span class="btn-label ms-1">Actualiser</span>
            </button>
            </div>
          </div>
        </template>
      </PrimevueDialog>
    </template>
  </AdminLayout>
</template>

<script>
import ProductForm from './ProductForm.vue';
import ProductMergeDialog from './ProductMergeDialog.vue';
import * as XLSX from 'xlsx';
import { appendPoweredByWorksheet } from '@/support/exportBranding';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { productImageUrl } from '@/support/productImages';
import { normalizeRayonColor } from '@/utils/rayonColor';
import { hasPermission } from '@/support/permissions';
import { bulkActionViewMixin } from '@/support/bulkActions';

const FILTER_MODULE = 'products';
const DEFAULT_FILTERS = {
  search: '',
  category_id: '',
  rayon_id: '',
};
const IMPORT_POLLING_DELAY_MS = 2000;
const IMPORT_POLLING_RETRY_DELAY_MS = 5000;
const IMPORT_POLLING_MAX_FAILURES = 3;

export default {
  name: 'ProductsView',
  mixins: [bulkActionViewMixin],
  components: { ProductForm, ProductMergeDialog },
  data() {
    return {
      products: [],
      productsResponse: null,
      isLoading: false,
      errorMessage: '',
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'photo', label: 'Photo', style: 'width: 80px;' },
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'barcode', label: 'Code-barres', sortable: true },
        { key: 'rayon', label: 'Rayon' },
        { key: 'categorie', label: 'Catégorie' },
        { key: 'sale_price', label: 'Prix vente', sortable: true, class: 'text-end' },
        { key: 'sale_price_coefficient', label: 'Coef.', sortable: true, class: 'text-end' },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-center' },
        { key: 'allow_negative_stock_sales', label: 'Stock nég.', class: 'text-center' },
      ],

      // ✅ filtres
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,

      // ✅ options filtres
      categories: [],
      rayons: [],
      suppliers: [],
      isLoadingFilters: false,

      dialogVisible: false,
      dialogMode: 'create',
      selectedProduct: null,
      mergeDialogVisible: false,

      importDialogVisible: false,
      importStep: 'guide',
      selectedFileName: '',
      parsedRows: [],
      selectedImportSupplierId: '',
      importResult: null,
      importPollingTimer: null,
      isImportStatusLoading: false,
      importCompletionHandled: false,
      importPollingFailureCount: 0,
      parseErrorMessage: '',
      isParsing: false,
      isImporting: false,
    };
  },
  mounted() {
    this.restoreFilters();
    this.fetchFilterOptions();
    this.fetchProducts();
  },
  beforeUnmount() {
    this.clearImportPolling();
  },
  computed: {
    previewRows() {
      return this.parsedRows.slice(0, 5);
    },

    importResultRows() {
      return (this.importResult && this.importResult.results) || [];
    },

    importProgressPercent() {
      const total = Number(this.importResult?.total_rows || this.parsedRows.length || 0);
      if (!total) return 0;

      const processed = Number(this.importResult?.processed_rows || 0);
      return Math.min(100, Math.round((processed / total) * 100));
    },

    categoryFilterOptions() {
      return [
        { label: 'Toutes', value: '' },
        ...this.categories.map((category) => ({
          label: category.name,
          value: String(category.id),
        })),
      ];
    },

    rayonFilterOptions() {
      return [
        { label: 'Tous', value: '' },
        ...this.rayons.map((rayon) => ({
          label: rayon.name,
          value: String(rayon.id),
          color: rayon.color,
        })),
      ];
    },

    selectedRayonFilterOption() {
      return this.rayonFilterOptions.find((option) => option.value === this.filters.rayon_id)
        || this.rayonFilterOptions[0];
    },

    supplierImportOptions() {
      return this.suppliers.map((supplier) => ({
        label: supplier.type === 'system' ? `${supplier.name} (système)` : supplier.name,
        value: String(supplier.id),
      }));
    },
    totalRows() {
      return extractApiTotal(this.productsResponse, this.products.length);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser']
        || this.$store?.getters?.['auth/user']
        || this.$store?.state?.auth?.user
        || null;
    },
    canCreateProduct() {
      return hasPermission(this.currentUser, 'products.create');
    },
    canImportProducts() {
      return hasPermission(this.currentUser, 'products.import');
    },
    canMergeProducts() {
      return hasPermission(this.currentUser, 'products.merge');
    },
    canDeleteProducts() {
      return hasPermission(this.currentUser, 'products.delete');
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    async bulkDeactivateProducts() {
      await this.performBulkAction({
        url: '/products/bulk/deactivate',
        method: 'patch',
        itemLabel: 'produit',
        confirmation: (count) => ({
          header: 'Désactiver les produits',
          message: `Désactiver les ${count} produits sélectionnés ?`,
          acceptLabel: `Désactiver ${count}`,
          acceptClass: 'p-button-danger',
        }),
        refresh: () => this.fetchProducts(),
      });
    },

    productImage(product) {
      return productImageUrl(product);
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      const values = state.values;
      this.filters = {
        search: values.search || '',
        category_id: values.category_id || '',
        rayon_id: values.rayon_id || '',
      };
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async fetchFilterOptions() {
      // ⚠️ adapte selon tes endpoints
      // Option A: GET /categories et GET /rayons
      // Option B: GET /product-categories et /product-rayons
      this.isLoadingFilters = true;
      try {
        // catégories
        try {
          const cRes = await this.$api.get('/categories');
          const cData = cRes.data.data || cRes.data || [];
          this.categories = cData.map((c) => ({ id: c.id, name: c.name }));
        } catch (e) {
          console.warn('Endpoint /categories introuvable (optionnel).', e);
          this.categories = [];
        }

        // rayons
        try {
          const rRes = await this.$api.get('/rayons');
          const rData = rRes.data.data || rRes.data || [];
          this.rayons = rData.map((r) => ({ id: r.id, name: r.name, color: r.color }));
        } catch (e) {
          console.warn('Endpoint /rayons introuvable (optionnel).', e);
          this.rayons = [];
        }

        try {
          const sRes = await this.$api.get('/suppliers', { params: { paginate: 0 } });
          const sData = sRes.data.data || sRes.data || [];
          this.suppliers = sData.map((supplier) => ({
            id: supplier.id,
            name: supplier.name,
            type: supplier.type || 'standard',
            system_key: supplier.system_key || null,
          }));
          this.selectDefaultImportSupplier();
        } catch (e) {
          console.warn('Endpoint /suppliers introuvable (optionnel).', e);
          this.suppliers = [];
        }
      } finally {
        this.isLoadingFilters = false;
      }
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchProducts();
    },

    async fetchProducts(options = {}) {
      const silent = Boolean(options?.silent);

      if (!silent) {
        this.isLoading = true;
        this.errorMessage = '';
      }

      try {
        const filterParams = cleanFilterParams({
          ...this.filters,
        });

        const params = {
          ...filterParams,
          paginate: 1,
          page: this.table.page,
          per_page: this.table.perPage,
          sort_by: this.table.sortBy,
          sort_dir: this.table.sortDir,
        };
        const requestConfig = { params };

        if (silent) {
          requestConfig.skipErrorToast = true;
        }

        const response = await this.$api.get('/products', requestConfig);
        this.productsResponse = response.data;
        this.products = extractApiList(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des produits', error);
        if (!silent) {
          this.errorMessage = 'Impossible de charger les produits.';
        }
      } finally {
        if (!silent) {
          this.isLoading = false;
        }
      }
    },

    openCreateDialog() {
      if (!this.canCreateProduct) return;

      this.dialogMode = 'create';
      this.selectedProduct = null;
      this.dialogVisible = true;
    },

    openEditDialog(product) {
      if (!hasPermission(this.currentUser, 'products.update')) return;

      this.dialogMode = 'edit';
      this.selectedProduct = { ...product };
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
    },

    async handleSaved() {
      this.dialogVisible = false;
      await this.fetchProducts();
    },

    openMergeDialog() {
      if (!this.canMergeProducts) return;

      this.mergeDialogVisible = true;
    },

    async handleMerged() {
      this.mergeDialogVisible = false;
      await this.fetchProducts();
    },

    async confirmDelete(product) {
      if (!this.canDeleteProducts) return;

      const ok = await this.$confirmAction(`Voulez-vous vraiment supprimer le produit "${product.name}" ?`);
      if (!ok) return;

      try {
        await this.$api.delete(`/products/${product.id}`);
        await this.fetchProducts();
      } catch (error) {
        console.error('Erreur lors de la suppression du produit', error);
        alert('Une erreur est survenue lors de la suppression du produit.');
      }
    },

    goToShow(product) {
      this.$router.push({ name: 'ProductShow', params: { id: product.id } });
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchProducts();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchProducts();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchProducts();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchProducts();
    },

    // ---- Import Excel ----
    openImportDialog() {
      if (!this.canImportProducts) return;

      this.importDialogVisible = true;
      this.importStep = 'guide';
      this.selectedFileName = '';
      this.parsedRows = [];
      this.selectDefaultImportSupplier();
      this.importResult = null;
      this.parseErrorMessage = '';
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;
      this.clearImportPolling();
    },
    selectDefaultImportSupplier() {
      const systemSupplier = this.suppliers.find((supplier) => (
        supplier.type === 'system' && supplier.system_key === 'default'
      )) || this.suppliers.find((supplier) => supplier.type === 'system');

      this.selectedImportSupplierId = systemSupplier ? String(systemSupplier.id) : '';
    },
    closeImportDialog() {
      this.importDialogVisible = false;
      this.importStep = 'guide';
      this.selectedFileName = '';
      this.parsedRows = [];
      this.selectedImportSupplierId = '';
      this.importResult = null;
      this.parseErrorMessage = '';
      this.isParsing = false;
      this.isImporting = false;
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;
      this.clearImportPolling();

      const input = this.$refs.fileInput;
      if (input && input.value !== undefined) {
        input.value = '';
      }
    },
    triggerFileSelect() {
      const input = this.$refs.fileInput;
      if (input && typeof input.click === 'function') input.click();
    },
    onFileChange(event) {
      const input = event.target;
      const file = input.files && input.files[0];
      if (!file) return;

      this.selectedFileName = file.name;
      this.parsedRows = [];
      this.importResult = null;
      this.parseErrorMessage = '';
      this.readExcelFile(file);
    },
    readExcelFile(file) {
      this.isParsing = true;
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });
          this.parsedRows = this.normalizeImportRows(jsonData);

          if (!this.parsedRows.length) {
            this.parseErrorMessage = 'Le fichier ne contient aucune ligne à importer.';
          }
        } catch (error) {
          console.error('Erreur lecture Excel', error);
          this.parseErrorMessage = 'Impossible de lire le fichier Excel. Vérifiez le format.';
        } finally {
          this.isParsing = false;
        }
      };

      reader.onerror = () => {
        this.isParsing = false;
        this.parseErrorMessage = 'Erreur lors de la lecture du fichier.';
      };

      reader.readAsArrayBuffer(file);
    },
    async submitImport() {
      if (!this.parsedRows.length) {
        alert("Veuillez sélectionner un fichier et vérifier qu'il contient des lignes.");
        return;
      }

      this.isImporting = true;
      this.importResult = null;
      this.importStep = 'results';
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;

      try {
        const payload = {
          rows: this.parsedRows,
        };

        if (this.selectedImportSupplierId) {
          payload.supplier_id = Number(this.selectedImportSupplierId);
        }

        const response = await this.$api.post('/products/import', payload);
        this.importResult = response.data.data;
        this.startImportPolling();
      } catch (error) {
        console.error('Erreur import produits', error);
        let msg = "Une erreur est survenue pendant l’import.";
        if (error?.response?.data?.errors) {
          const raw = Object.values(error.response.data.errors);
          const flat = raw.reduce((acc, v) => acc.concat(v), []);
          msg = flat.join(' ');
        }
        this.importResult = {
          status: 'failed',
          error_message: msg,
          total_rows: this.parsedRows.length,
          processed_rows: 0,
          imported_count: 0,
          skipped_count: 0,
          failed_count: 0,
          results: [],
        };
        this.isImporting = false;
      }
    },

    async fetchImportStatus(options = {}) {
      const automatic = Boolean(options?.automatic);
      const importId = this.importResult?.id;

      if (!importId || this.isImportStatusLoading || this.importCompletionHandled) return;

      this.isImportStatusLoading = true;
      try {
        const response = await this.$api.get(`/products/imports/${importId}`, { skipErrorToast: true });

        if (!this.importResult || this.importResult.id !== importId) return;

        this.importPollingFailureCount = 0;
        this.importResult = response.data.data;

        if (['completed', 'failed'].includes(this.importResult.status)) {
          this.isImporting = false;
          this.clearImportPolling();

          if (this.importCompletionHandled) return;

          this.importCompletionHandled = true;

          if (this.importResult.status === 'completed') {
            await this.fetchProducts({ silent: true });
          }

          return;
        }

        if (automatic || this.isImporting) {
          this.scheduleImportPolling();
        }
      } catch (error) {
        console.error('Erreur suivi import produits', error);

        if (!this.importResult || this.importResult.id !== importId) return;

        this.importPollingFailureCount += 1;

        if (this.importPollingFailureCount >= IMPORT_POLLING_MAX_FAILURES) {
          this.isImporting = false;
          this.clearImportPolling();
          this.errorMessage = 'Le suivi automatique de l’import est temporairement indisponible. Vous pouvez actualiser manuellement.';

          return;
        }

        if (automatic || this.isImporting) {
          this.scheduleImportPolling(IMPORT_POLLING_RETRY_DELAY_MS);
        }
      } finally {
        if (this.importResult?.id === importId) {
          this.isImportStatusLoading = false;
        }
      }
    },

    startImportPolling() {
      this.clearImportPolling();
      this.isImporting = true;
      this.isImportStatusLoading = false;
      this.importCompletionHandled = false;
      this.importPollingFailureCount = 0;
      this.fetchImportStatus({ automatic: true });
    },

    scheduleImportPolling(delay = IMPORT_POLLING_DELAY_MS) {
      this.clearImportPolling();

      if (!this.isImporting || !this.importResult?.id || this.importCompletionHandled) return;

      this.importPollingTimer = window.setTimeout(() => {
        this.importPollingTimer = null;
        this.fetchImportStatus({ automatic: true });
      }, delay);
    },

    clearImportPolling() {
      if (this.importPollingTimer) {
        window.clearTimeout(this.importPollingTimer);
        this.importPollingTimer = null;
      }
    },

    normalizeImportRows(rows) {
      return (rows || [])
        .map((row, index) => {
          const normalized = {};

          Object.entries(row || {}).forEach(([key, value]) => {
            normalized[this.normalizeImportKey(key)] = value;
          });

          const mapped = {
            __rowNum__: row.__rowNum__ || index + 2,
            name: this.pickImportValue(normalized, ['name', 'nom', 'nom_produit', 'produit', 'designation', 'libelle']),
            sku: this.pickImportValue(normalized, ['sku', 'code', 'reference', 'ref', 'code_produit']),
            barcode: this.pickImportValue(normalized, ['barcode', 'code_barre', 'codebarre', 'ean', 'ean13', 'gtin']),
            photo: this.pickImportValue(normalized, ['photo', 'image', 'url_photo', 'photo_url']),
            description: this.pickImportValue(normalized, ['description', 'desc']),
            purchase_price: this.pickImportValue(normalized, ['purchase_price', 'prix_achat', 'prix_d_achat', 'pa', 'achat']),
            sale_price: this.pickImportValue(normalized, ['sale_price', 'prix_vente', 'prix_de_vente', 'pv', 'vente']),
            sale_price_coefficient: this.pickImportValue(normalized, ['sale_price_coefficient', 'coefficient', 'coef', 'coefficient_prix_vente']),
            is_tva_applicable: this.pickImportValue(normalized, ['is_tva_applicable', 'tva', 'avec_tva', 'soumis_tva']),
            is_css_applicable: this.pickImportValue(normalized, ['is_css_applicable', 'css', 'avec_css', 'soumis_css']),
            stock_quantity: this.pickImportValue(normalized, ['stock_quantity', 'stock', 'quantite', 'quantite_stock', 'qte', 'qty']),
            min_stock_quantity: this.pickImportValue(normalized, ['min_stock_quantity', 'stock_minimum', 'stock_min', 'min_stock', 'seuil', 'seuil_stock']),
            allow_negative_stock_sales: this.pickImportValue(normalized, [
              'allow_negative_stock_sales',
              'vente_stock_negatif',
              'ventes_stock_negatif',
              'autoriser_stock_negatif',
              'autorise_stock_negatif',
              'stock_negatif',
            ]),
            rayon: this.pickImportValue(normalized, ['rayon', 'rayon_name', 'nom_rayon']),
            categorie: this.pickImportValue(normalized, ['categorie', 'category', 'category_name', 'nom_categorie']),
            supplier_sku: this.pickImportValue(normalized, ['supplier_sku', 'reference_fournisseur', 'ref_fournisseur', 'sku_fournisseur']),
            lead_time_days: this.pickImportValue(normalized, ['lead_time_days', 'delai_livraison', 'delai_de_livraison', 'delai']),
            min_order_quantity: this.pickImportValue(normalized, ['min_order_quantity', 'quantite_minimale', 'qte_min', 'moq']),
            is_active: this.pickImportValue(normalized, ['is_active', 'actif', 'active', 'statut']),
          };

          return mapped;
        })
        .filter((row) => Object.entries(row).some(([key, value]) => {
          return key !== '__rowNum__' && value !== '' && value !== null && value !== undefined;
        }));
    },

    normalizeImportKey(key) {
      return String(key || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    },

    pickImportValue(row, keys) {
      const key = keys.find((candidate) => row[candidate] !== undefined && row[candidate] !== null && row[candidate] !== '');
      return key ? row[key] : '';
    },

    importStatusLabel(status) {
      if (status === 'queued') return 'En attente';
      if (status === 'processing') return 'En cours';
      if (status === 'completed') return 'Terminé';
      if (status === 'failed') return 'Échec';
      return status || 'Non lancé';
    },

    importStatusClass(status) {
      if (status === 'completed') return 'bg-success';
      if (status === 'failed') return 'bg-danger';
      if (status === 'processing') return 'bg-primary';
      return 'bg-secondary';
    },

    importRowStatusLabel(status) {
      if (status === 'imported') return 'Importé';
      if (status === 'skipped') return 'Ignoré';
      if (status === 'failed') return 'Erreur';
      return status || '-';
    },

    importRowStatusClass(status) {
      if (status === 'imported') return 'bg-success';
      if (status === 'skipped') return 'bg-warning text-dark';
      if (status === 'failed') return 'bg-danger';
      return 'bg-secondary';
    },

    downloadTemplate() {
      const headers = [
        'nom',
        'sku',
        'code_barre',
        'prix_vente',
        'prix_achat',
        'coefficient',
        'tva',
        'css',
        'stock',
        'stock_minimum',
        'vente_stock_negatif',
        'rayon',
        'categorie',
        'description',
        'photo_url',
        'reference_fournisseur',
        'delai_livraison',
        'quantite_minimale',
        'actif',
      ];
      const exampleRow = [
        'Riz parfumé 5kg',
        'RIZ-PARF-5KG',
        '3760000000012',
        7500,
        5600,
        1.34,
        'oui',
        'non',
        40,
        5,
        'non',
        'Épicerie',
        'Riz et céréales',
        'Sac de riz parfumé format familial',
        'https://example.com/images/riz-parfume-5kg.jpg',
        'FOUR-RIZ-5KG',
        7,
        10,
        'oui',
      ];

      const worksheet = XLSX.utils.aoa_to_sheet([headers, exampleRow]);
      appendPoweredByWorksheet(worksheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Produits');

      XLSX.writeFile(workbook, 'modele-produits-suite241-gestion-commerciale.xlsx');
    },
  },
};
</script>

<style scoped>
.import-steps {
  display: flex;
  gap: 0.5rem;
}

.step-pill {
  border: 1px solid #d6d6d6;
  border-radius: 999px;
  color: #6c757d;
  font-size: 0.85rem;
  padding: 0.35rem 0.75rem;
}

.step-pill.active {
  background: var(--bs-primary);
  border-color: var(--bs-primary);
  color: #fff;
}

.import-results-table {
  max-height: 320px;
}

.rayon-option,
.rayon-badge {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.rayon-badge {
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.75rem;
  width: 0.75rem;
}
</style>
